import { useState, useEffect, useRef } from "react";
import { Switch, TextField, Tooltip, IconButton, CircularProgress } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../../UniversalComponents/DefaultButton";
import { addUpdateGroupMember } from "../../../../../utils/api/managePermissionApi";
import { pushAlert } from "../../../../../utils/alertSlice";
import { useDispatch } from "react-redux";
import LabelComponent from "../../../../UniversalComponents/LabelComponent";
import Loader from "../../../../UniversalComponents/Loader";

export default function AboutPanel({ onClose, groupData, setRefetch }) {
  const [form, setForm] = useState({
    groupName: "",
    description: "",
    permissions: {},
    views: {},
  });
  const [permissionList, setPermissionList] = useState([]);
  const [viewList, setViewList] = useState([]);
  const [loading, setLoading] = useState(false); // ✅ loader state
  const dispatch = useDispatch();
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!groupData) return;
    setLoading(true); // ✅ start loader

    // Simulate a small delay for smoother UX (optional)
    setTimeout(() => {
      drawerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

      const permissions = {};
      groupData?.permissions?.forEach((perm) => {
        permissions[perm?.id] = perm?.is_enabled ? true : false;
      });

      const views = {};
      groupData?.tracking_views?.forEach((view) => {
        views[view?.id] = view?.is_enabled ? true : false;
      });

      setForm({
        groupName: groupData.group_name || "",
        description: groupData.about_group || "",
        permissions,
        views,
      });

      setPermissionList(groupData?.permissions || []);
      setViewList(groupData?.tracking_views || []);

      setLoading(false); // ✅ stop loader
    }, 300); // can be 0 if you want instant
  }, [groupData]);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const handleTogglePermission = (permKey) =>
    setForm((prev) => ({ ...prev, permissions: { ...prev.permissions, [permKey]: !prev.permissions[permKey] } }));
  const handleToggleView = (permKey) =>
    setForm((prev) => ({ ...prev, views: { ...prev.views, [permKey]: !prev.views[permKey] } }));

  const handleSave = async () => {
    try {
      if (!form?.groupName?.trim() || !form?.description?.trim()) {
        dispatch(pushAlert({ visible: true, message: "Group Name and Description are required.", type: "danger" }));
        return;
      }

      if (!form?.views || Object.keys(form.views).length === 0) {
        dispatch(pushAlert({ visible: true, message: "At least one view must be selected.", type: "danger" }));
        return;
      }

      if (!form?.permissions || Object.keys(form.permissions).length === 0) {
        dispatch(pushAlert({ visible: true, message: "At least one permission must be selected.", type: "danger" }));
        return;
      }

      const group = {
        group_id: groupData?.group_id,
        group_name: form.groupName,
        group_description: form.description,
        views: Object.keys(form.views).filter((viewId) => form.views[viewId]),
        modules: Object.keys(form.permissions).map((permissionId) => ({
          module_id: Number(permissionId),
          module_permission: form.permissions[permissionId] ? 1 : 0,
        })),
      };

      const response = await addUpdateGroupMember({ body: group });

      if (response?.success) {
        dispatch(pushAlert({ visible: true, message: response.message, type: "success" }));
        setRefetch((prev) => !prev);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Failed to save group.", type: "danger" }));
      }
    } catch (err) {
      dispatch(pushAlert({ visible: true, message: err?.message || "Unexpected error occurred.", type: "danger" }));
    }
  };

  return (
    <div className="border-[1px] border-gray-200 h-full flex flex-col gap-4 justify-between p-4">
      <div className="flex flex-col min-h-0 grow gap-4">
        {/* Header */}
        <div className="flex items-center justify-between" ref={drawerRef}>
          <h2 className="text-lg font-semibold">About</h2>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <div className="flex flex-col min-h-0 grow gap-4 overflow-auto">
          {/* Group Name */}
          <div>
            <LabelComponent labelText="Group Name" isMandatory={true} size="small" />
            <TextField
              fullWidth
              size="small"
              value={form.groupName}
              placeholder="Group Name"
              onChange={(e) => handleChange("groupName", e.target.value)}
              disabled={groupData?.is_locked === 1}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  cursor: groupData?.is_locked === 1 ? "not-allowed" : "text",
                },
              }}
            />
          </div>

          {/* Description */}
          <div>
            <LabelComponent labelText="Description" isMandatory={true} size="small" />
            <TextField
              placeholder="Description"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={groupData?.is_locked === 1}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  cursor: groupData?.is_locked === 1 ? "not-allowed" : "text",
                },
              }}
            />
          </div>

          {/* Permissions */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <p className="font-medium">Enabled Permissions</p>
              <Tooltip title="Toggle the features available to this group">
                <InfoOutlinedIcon fontSize="small" className="text-gray-500" />
              </Tooltip>
            </div>
            <div className="space-y-2">
              {permissionList.map((perm) => (
                <div key={perm?.id} className="flex justify-between items-center bg-gray-100 rounded-md px-3 py-2">
                  <span>{perm?.name}</span>
                  <Switch
                    checked={form.permissions[perm?.id]}
                    onChange={() => handleTogglePermission(perm?.id)}
                    size="small"
                    disabled={groupData?.is_locked === 1}
                    sx={{ cursor: groupData?.is_locked === 1 ? "not-allowed" : "pointer" }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Views */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <p className="font-medium">Views</p>
              <Tooltip title="Toggle the features available to this group">
                <InfoOutlinedIcon fontSize="small" className="text-gray-500" />
              </Tooltip>
            </div>
            <div className="space-y-2">
              {viewList.map((view) => (
                <div key={view?.id} className="flex justify-between items-center bg-gray-100 rounded-md px-3 py-2">
                  <span>{view?.name}</span>
                  <Switch
                    checked={form.views[view?.id]}
                    onChange={() => handleToggleView(view?.id)}
                    size="small"
                    disabled={groupData?.is_locked === 1}
                    sx={{ cursor: groupData?.is_locked === 1 ? "not-allowed" : "pointer" }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {groupData?.is_locked === 0 && (
        <div className="flex justify-end">
          <Button onClick={handleSave}>Save</Button>
        </div>
      )}
      <Loader isOpen={loading} />
    </div>
  );
}
